// CorpusIndexCard — painel "Fontes de Conhecimento" (sprint apolo-corpus,
// 06-DESIGN). Status do índice LOCAL do vault + ações Mapear/Reindexar +
// denylist editável + badge de privacidade. Minimal, tokens do design system,
// hover neutro (cor só no estado ativo), UMA região aria-live polite.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Database, FolderPlus, Lock, RefreshCcw, Trash2 } from 'lucide-react';
import { listCorpusSources } from '../../../../lib/corpusSources.js';
import { isAmbientEnabled, setAmbientEnabled } from '../../../../lib/ambientBrain.js';

const STATUS_LABEL = {
  none: 'não mapeado',
  indexed: 'indexado',
  stale: 'desatualizado',
  building: 'mapeando…',
  error: 'erro',
};
const DOT_CLASS = {
  indexed: 'bg-emerald-500',
  building: 'bg-amber-400 animate-pulse',
  stale: 'bg-amber-400',
  error: 'bg-red-500',
  none: 'bg-apolo-borderSubtle',
};

export default function CorpusIndexCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [announce, setAnnounce] = useState('');
  const [exclusionsDraft, setExclusionsDraft] = useState(null);
  const [ambientOn, setAmbientOn] = useState(() => isAmbientEnabled());
  const [folders, setFolders] = useState([]);
  const mounted = useRef(true);

  const corpusApi = typeof window !== 'undefined' ? window.apolo?.corpus : null;

  const load = useCallback(async () => {
    if (!corpusApi?.status) return;
    try {
      const st = await corpusApi.status();
      if (mounted.current) setStatus(st);
      if (corpusApi.list) {
        const all = await corpusApi.list();
        if (mounted.current) setFolders((all || []).filter((c) => c.kind === 'folder'));
      }
    } catch { /* IPC fora — card mostra indisponível */ }
  }, [corpusApi]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  const run = useCallback(async (action, label) => {
    if (!corpusApi?.[action] || busy) return;
    setBusy(true);
    setAnnounce(`${label} iniciado`);
    try {
      const res = await corpusApi[action]();
      setAnnounce(res?.ok ? `${label} concluído` : `${label} falhou: ${res?.error || ''}`);
    } catch (e) {
      setAnnounce(`${label} falhou: ${e?.message || e}`);
    } finally {
      setBusy(false);
      load();
    }
  }, [corpusApi, busy, load]);

  const saveExclusions = useCallback(async () => {
    if (!corpusApi?.setExclusions || exclusionsDraft == null) return;
    const patterns = exclusionsDraft.split(',').map((p) => p.trim()).filter(Boolean);
    await corpusApi.setExclusions(patterns);
    setExclusionsDraft(null);
    setAnnounce('Pastas excluídas salvas — reindexe pra aplicar');
    load();
  }, [corpusApi, exclusionsDraft, load]);

  const addFolder = useCallback(async () => {
    if (!corpusApi?.addFolder || busy) return;
    setBusy(true);
    setAnnounce('Escolha a pasta…');
    try {
      const res = await corpusApi.addFolder();
      setAnnounce(res?.ok ? `Pasta indexada: ${res.id} (${res.chunkCount} trechos)` : (res?.error === 'cancelado' ? '' : `Falhou: ${res?.error || ''}`));
    } catch (e) {
      setAnnounce(`Falhou: ${e?.message || e}`);
    } finally {
      setBusy(false);
      load();
    }
  }, [corpusApi, busy, load]);

  const removeFolder = useCallback(async (id, name) => {
    if (!corpusApi?.removeCorpus) return;
    if (!window.confirm(`Remover o corpus "${name}"? O índice local será apagado (a pasta original fica intacta).`)) return;
    await corpusApi.removeCorpus(id);
    setAnnounce(`Corpus ${name} removido`);
    load();
  }, [corpusApi, load]);

  const vault = listCorpusSources().find((s) => s.id === 'vault');

  if (!corpusApi) {
    return (
      <div className="m-3 rounded-lg border border-apolo-borderSubtle bg-apolo-surface1 p-3 text-xs text-apolo-textTertiary">
        Índice local disponível só no app desktop (Electron).
      </div>
    );
  }

  const st = status || { status: 'none', chunkCount: 0, exclusions: [] };
  const effective = busy ? 'building' : st.status || 'none';
  const indexed = effective === 'indexed' || (st.chunkCount > 0 && effective !== 'building');

  return (
    <div className="m-3 rounded-lg border border-apolo-borderSubtle bg-apolo-surface1 p-3" aria-label="Fontes de Conhecimento">
      <div aria-live="polite" className="sr-only">{announce}</div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-apolo-textPrimary">
          <Database size={12} aria-hidden="true" className="text-apolo-textTertiary" />
          <span className="font-medium">{vault?.name}</span>
          <span className={`inline-block h-2 w-2 rounded-full ${DOT_CLASS[effective] || DOT_CLASS.none}`} aria-hidden="true" />
          <span className="text-apolo-textTertiary">{STATUS_LABEL[effective] || effective}</span>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border border-apolo-borderSubtle px-2 py-0.5 text-[10px] text-apolo-textTertiary">
          <Lock size={9} aria-hidden="true" />
          {vault?.privacy}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-apolo-textTertiary">
        <span>{st.chunkCount || 0} trechos</span>
        {st.builtAt && <span>atualizado {new Date(st.builtAt).toLocaleString('pt-BR')}</span>}
        {st.watching && <span>auto-reindex ativo</span>}
        {st.lastError && <span className="text-red-400">erro: {st.lastError}</span>}
      </div>

      <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] text-apolo-textSecondary">
        <input
          type="checkbox"
          checked={ambientOn}
          onChange={(e) => { setAmbientOn(e.target.checked); setAmbientEnabled(e.target.checked); setAnnounce(e.target.checked ? 'Brain ambiente ligado' : 'Brain ambiente desligado'); }}
          className="h-3 w-3 accent-apolo-accent focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
        />
        Brain ambiente
        <span className="text-[10px] text-apolo-textTertiary">injeta contexto do vault em turnos relevantes</span>
      </label>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => run('build', indexed ? 'Re-mapeamento' : 'Mapeamento')}
          disabled={busy}
          aria-busy={busy}
          className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2.5 py-1 text-xs text-apolo-textPrimary transition hover:bg-apolo-surface3 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 disabled:opacity-50"
        >
          <RefreshCcw size={11} aria-hidden="true" className={busy ? 'animate-spin' : ''} />
          {indexed ? 'Reindexar' : 'Mapear vault'}
        </button>
        {indexed && (
          <button
            type="button"
            onClick={() => run('refresh', 'Refresh incremental')}
            disabled={busy}
            className="rounded-md px-2 py-1 text-xs text-apolo-textTertiary transition hover:text-apolo-textSecondary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 disabled:opacity-50"
          >
            Só o delta
          </button>
        )}
      </div>

      <div className="mt-3 border-t border-apolo-borderSubtle pt-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-apolo-textTertiary">Outras fontes (pastas locais):</span>
          <button
            type="button"
            onClick={addFolder}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-apolo-textTertiary transition hover:text-apolo-textSecondary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 disabled:opacity-50"
          >
            <FolderPlus size={10} aria-hidden="true" />
            Adicionar pasta
          </button>
        </div>
        {folders.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1">
            {folders.map((f) => (
              <li key={f.id} className="flex items-center gap-2 rounded-md border border-apolo-borderSubtle px-2 py-1 text-[11px]">
                <span className="truncate text-apolo-textSecondary">{f.name}</span>
                <span className="text-[10px] text-apolo-textTertiary">{f.chunkCount} trechos</span>
                <span className="inline-flex items-center gap-0.5 rounded-full border border-apolo-borderSubtle px-1.5 text-[9px] text-apolo-textTertiary"><Lock size={8} aria-hidden="true" />local</span>
                <button
                  type="button"
                  onClick={() => removeFolder(f.id, f.name)}
                  aria-label={`Remover corpus ${f.name}`}
                  className="ml-auto rounded p-0.5 text-apolo-textTertiary transition hover:text-apolo-textSecondary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
                >
                  <Trash2 size={10} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-3 border-t border-apolo-borderSubtle pt-2">
        <div className="text-[11px] text-apolo-textTertiary">Pastas excluídas (nunca indexadas):</div>
        {exclusionsDraft == null ? (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {(st.exclusions || []).map((p) => (
              <span key={p} className="rounded-full border border-apolo-borderSubtle px-2 py-0.5 text-[10px] text-apolo-textSecondary">{p}</span>
            ))}
            <button
              type="button"
              onClick={() => setExclusionsDraft((st.exclusions || []).join(', '))}
              className="rounded px-1.5 py-0.5 text-[10px] text-apolo-textTertiary transition hover:text-apolo-textSecondary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
            >
              editar
            </button>
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-1">
            <input
              type="text"
              value={exclusionsDraft}
              onChange={(e) => setExclusionsDraft(e.target.value)}
              aria-label="Pastas excluídas, separadas por vírgula"
              className="min-w-0 flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-[11px] text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
            />
            <button type="button" onClick={saveExclusions} className="rounded-md border border-apolo-borderSubtle px-2 py-1 text-[10px] text-apolo-textSecondary transition hover:bg-apolo-surface3 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50">Salvar</button>
            <button type="button" onClick={() => setExclusionsDraft(null)} className="rounded-md px-1.5 py-1 text-[10px] text-apolo-textTertiary transition hover:text-apolo-textSecondary focus:outline-none">cancelar</button>
          </div>
        )}
      </div>
    </div>
  );
}
